import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import type { Order, OrderStatus } from "../../lib/types";

const fmt = (n: number) => n.toLocaleString("ko-KR");
const TRANSITIONS: Record<OrderStatus, OrderStatus[]> = {
  pending_payment: ["paid", "cancelled"],
  paid: ["preparing", "cancelled"],
  preparing: ["shipping", "pickup_ready", "cancelled"],
  shipping: ["completed"],
  pickup_ready: ["completed"],
  completed: [],
  cancelled: [],
};

export default function AdminOrders() {
  const [orders, setOrders] = useState<(Order & { company_name: string })[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get<{ items: (Order & { company_name: string })[]; total: number }>("/admin/orders?limit=50")
      .then((r) => { setOrders(r.items); setTotal(r.total); })
      .finally(() => setLoading(false));
  }, []);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const r = await api.get<{ items: (Order & { company_name: string })[]; total: number }>(`/admin/orders?limit=50&offset=${orders.length}`);
      setOrders((prev) => [...prev, ...r.items]);
      setTotal(r.total);
    } finally { setLoadingMore(false); }
  };

  useEffect(() => { load(); }, [load]);

  const [busyId, setBusyId] = useState<string | null>(null);
  const updateStatus = async (id: string, status: OrderStatus) => {
    setBusyId(id);
    try {
      await api.post(`/admin/orders/${id}/status`, { status });
      load();
    } finally { setBusyId(null); }
  };

  return (
    <>
    <div className="card">
      <table>
        <thead><tr>
          <th>주문번호</th><th>업체</th><th>수령</th><th>결제</th><th style={{ textAlign: "right" }}>금액</th><th>상태</th>
        </tr></thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={6} style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>로딩 중...</td></tr>
          ) : orders.length === 0 ? (
            <tr><td colSpan={6} style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>주문이 없습니다.</td></tr>
          ) : orders.map((o) => (
            <tr key={o.id}>
              <td style={{ fontFamily: "monospace", fontWeight: 600 }}>{o.order_no}
                <div className="muted" style={{ fontSize: 15, fontFamily: "var(--font-sans)" }}>
                  {new Date(o.created_at * 1000).toLocaleString("ko-KR")}
                </div>
              </td>
              <td>{o.company_name}</td>
              <td style={{ fontSize: 15 }}>{o.fulfillment === "delivery" ? "🚚 배송" : "🏪 수령"}</td>
              <td style={{ fontSize: 15 }}>{o.payment_method}</td>
              <td style={{ textAlign: "right", fontWeight: 600 }}>{fmt(o.total)}원</td>
              <td>
                {(TRANSITIONS[o.status]?.length ?? 0) > 0 ? (
                  <select value="" disabled={busyId === o.id} onChange={(e) => { if (e.target.value) updateStatus(o.id, e.target.value as OrderStatus); }} style={{ width: "auto", padding: "4px 8px", fontSize: 15 }}>
                    <option value="">{o.status}</option>
                    {TRANSITIONS[o.status].map((s) => <option key={s} value={s}>→ {s}</option>)}
                  </select>
                ) : (
                  <span className={`badge ${o.status === "completed" ? "badge-success" : "badge-muted"}`}>{o.status}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {!loading && orders.length < total && (
      <div style={{ textAlign: "center", marginTop: 16 }}>
        <button className="btn btn-outline btn-sm" onClick={loadMore} disabled={loadingMore}>
          {loadingMore ? "불러오는 중..." : `더 보기 (${orders.length}/${total})`}
        </button>
      </div>
    )}
    </>
  );
}
